import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

export function RegisterForm() {
  const { t } = useTranslation();
  const history = useHistory();
  const validator = useValidator();

  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG;
  const organizationName = organizationSlug
    ? organizationSlug
        .split("-")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "Boomer";

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      motivation: "",
    },
    onSubmit: async ({ value: _ }) => {
      try {
        // Navigate to success page
        history.push(Routes.AuthRegisterSuccess());
      } catch (error) {
        logger.error("Registration error:", error);
        toast.error(t("fields:register.error"));
      }
    },
  });

  return (
    <FormElement className="flex flex-col gap-6">
      {/* Organization-specific welcome message */}
      <div className="rounded-lg bg-brand-50 p-4">
        <p className="text-sm text-brand-900">
          Je registreert je voor flexibel werk bij <strong>{organizationName}</strong>. Na
          registratie beoordeelt ons team je aanmelding.
        </p>
      </div>

      <form.AppField
        name="fullName"
        validators={{
          onBlur: validator.required({ name: "fullName" }),
          onSubmit: validator.required({ name: "fullName" }),
        }}
        children={(field) => (
          <field.InputField
            type="text"
            autocomplete="name"
            enterkeyhint="next"
            label={t("fields:fullName.label")}
            placeholder={t("fields:register.fullname-placeholder")}
            required
          />
        )}
      />
      <form.AppField
        name="email"
        validators={{
          onBlur: validator.required({ name: "email" }),
          onSubmit: validator.email({ schema: validator.required({ name: "email" }) }),
        }}
        children={(field) => (
          <field.InputField
            type="email"
            autocomplete="email"
            enterkeyhint="next"
            label={t("fields:email.label")}
            placeholder={t("fields:register.email-placeholder")}
            required
          />
        )}
      />
      <form.AppField
        name="phone"
        validators={{
          onBlur: validator.required({ name: "phone" }),
          onSubmit: validator.required({ name: "phone" }),
        }}
        children={(field) => (
          <field.InputField
            type="tel"
            autocomplete="tel"
            enterkeyhint="next"
            label="Telefoonnummer"
            placeholder={t("fields:register.phone-placeholder")}
            required
          />
        )}
      />
      <form.AppField
        name="motivation"
        validators={{
          onBlur: validator.required({ name: "motivation" }),
          onSubmit: validator.required({ name: "motivation" }),
        }}
        children={(field) => (
          <field.TextareaField
            label={t("fields:register.motivation-label", { organizationName })}
            placeholder={t("fields:register.motivation-placeholder", { organizationName })}
            required
            rows={4}
          />
        )}
      />
      <form.Subscribe
        selector={(state) => [state.isSubmitting] as const}
        children={([isSubmitting]) => (
          <AuthSubmitButton type="submit" isSubmitting={isSubmitting}>
            Registreren bij {organizationName}
          </AuthSubmitButton>
        )}
      />
    </FormElement>
  );
}
